import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { APIRequestStatus } from "common-ts/dist/models/APIRequestStatus";

const FIN_GETTER_URL = 'https://8mav2hp6ki.execute-api.ap-southeast-1.amazonaws.com/OrderAPIProduction/fin-getter';
const FIN_UPDATER_URL = 'https://8mav2hp6ki.execute-api.ap-southeast-1.amazonaws.com/OrderAPIProduction/fin-updater';

const initialState = {
    finTrackerList: [],
    broadcastCampaignList: [],
    // Available (businessLine, branch) dimensions for the selector, and the one currently shown.
    // selectedDimensionId '' = consolidated (the legacy FinTrackerTable).
    dimensions: [] as { dimensionId: string; businessLineId: string; branchId: string }[],
    selectedDimensionId: '',
    // Optional global chart date window (YYYY-MM-DD). Empty strings = use each
    // chart's own default window (the current behaviour).
    chartStartDate: '',
    chartEndDate: '',
    // The startDate we last asked the backend for (YYYY-MM-DD). Empty = the backend's
    // default lookback. Used to decide when the user needs a deeper (older) refetch.
    loadedStartDate: '',
    status: APIRequestStatus.Idle,
    error: null
};
// Loads the day rows for the currently-selected dimension (or the consolidated FinTrackerTable
// when none is selected). Optionally pass a startDate (YYYY-MM-DD) to load older history; without
// it the backend returns its default lookback (~13 months). The requested startDate is preserved
// on action.meta.arg so the reducer can record how deep we've loaded.
export const fetchFinTrackerList = createAsyncThunk('finTrackerList/fetchFinTrackerList', async (startDate: string | undefined, thunkAPI) => {
    const state: any = thunkAPI.getState();
    const dimensionId = state?.finTracker?.selectedDimensionId || undefined;
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (dimensionId) params.dimensionId = dimensionId;
    const response = await axios.get(FIN_GETTER_URL, Object.keys(params).length ? { params } : undefined);

    return response.data; // tidak perlu JSON.parse
})
// Loads the list of dimensions that have recent rows (for the dimension selector).
export const fetchDimensionList = createAsyncThunk('finTrackerList/fetchDimensionList', async () => {
    const response = await axios.get(FIN_GETTER_URL, { params: { 'list-dimensions': 'true' } });
    return response.data || [];
})
export const fetchBroadcastCampaignList = createAsyncThunk('finTrackerList/fetchBroadcastCampaignList', async () => {
    const response = await axios.get(
    'https://8mav2hp6ki.execute-api.ap-southeast-1.amazonaws.com/OrderAPIProduction/broadcast-campaign'
    );

    return (response.data.campaignList); // tidak perlu JSON.parse
})

// Posts per-date ad spend edits for the currently-selected dimension (or the consolidated table
// when none is selected), triggers a recalculation from the earliest changed date on the backend,
// then re-fetches so every chart in Data Penjualan updates.
export const postUpdateAdSpendAndRecalculate = createAsyncThunk(
    'finTrackerList/postUpdateAdSpendAndRecalculate',
    async (adSpendUpdates: { [date: string]: number }, thunkAPI) => {
        const state: any = thunkAPI.getState();
        const dimensionId = state?.finTracker?.selectedDimensionId || undefined;
        const response = await axios({
            method: 'post',
            url: FIN_UPDATER_URL,
            data: JSON.stringify({ adSpendUpdates, dimensionId })
        });

        // Preserve the currently-loaded depth so a recalc doesn't collapse the view back
        // to the default lookback if the user had loaded older history.
        const loadedStartDate = state?.finTracker?.loadedStartDate || undefined;
        await thunkAPI.dispatch(fetchFinTrackerList(loadedStartDate));

        return response.data;
    }
)

export const finTrackerSlice = createSlice({
    name: 'finTrackerList',
    initialState,
    reducers: {
        setChartDateRange: (state, action) => {
            state.chartStartDate = action.payload.startDate ?? '';
            state.chartEndDate = action.payload.endDate ?? '';
        },
        clearChartDateRange: (state) => {
            state.chartStartDate = '';
            state.chartEndDate = '';
        },
        setSelectedDimension: (state, action) => {
            state.selectedDimensionId = action.payload || '';
        },
    },
    extraReducers(builder) {
        builder
            .addCase(fetchFinTrackerList.pending, (state, action) => {
                state.status = APIRequestStatus.RequestInProgress;
            })
            .addCase(fetchFinTrackerList.fulfilled, (state, action) => {
                state.status = APIRequestStatus.Success;

                state.finTrackerList = action.payload.sort(
                    (a: any, b: any) =>
                    new Date(a.creationDateTime).getTime() -
                    new Date(b.creationDateTime).getTime()
                );

                // Record how deep we've loaded: the requested startDate if given, else the
                // earliest date actually returned (the backend's default lookback boundary).
                state.loadedStartDate =
                    action.meta.arg ||
                    state.finTrackerList[0]?.creationDateTime ||
                    '';
            })
            .addCase(fetchFinTrackerList.rejected, (state, action) => {
                state.status = APIRequestStatus.Failure;
            })
            .addCase(fetchBroadcastCampaignList.pending, (state, action) => {
            })
            .addCase(fetchBroadcastCampaignList.fulfilled, (state, action) => {
                state.broadcastCampaignList = action.payload;
            })
            .addCase(fetchBroadcastCampaignList.rejected, (state, action) => {
            })
            .addCase(fetchDimensionList.fulfilled, (state, action) => {
                state.dimensions = action.payload;
            })
    }
})

export const { setChartDateRange, clearChartDateRange, setSelectedDimension } = finTrackerSlice.actions;

export default finTrackerSlice.reducer